import { useState } from "react";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { BaseService } from "../../../../../services/base-service";
import { URLS } from "../../../../../services/app-urls";
import { translate } from "../../../../../translate/translate";
import { ErrorComponent } from "../../../../../components/error/error.component";
import { RfcType } from "../../../../../types";

type SapImportResponse = {
  task_id?: string;
};

export const useImportRfcSap = (
  rfcType?: RfcType,
  onSuccess?: () => void,
  onTaskCreated?: (taskId?: string) => void,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const rfcService = new BaseService(URLS.RFC_EXECUTIONS);
  const forecastService = new BaseService(URLS.IMPORT_FORECAST_FROM_SAP);
  const stockPoService = new BaseService(URLS.IMPORT_STOCK_FROM_SAP);
  const bomService = new BaseService(URLS.IMPORT_BOM_FROM_SAP);

  const showRrcFunctionName = () => {
    switch (rfcType) {
      case RfcType.BOM:
        return "RFC_BOM_DATA";
      case RfcType.STOCK_AND_PO:
        return "RFC_STOCK_AND_PO";
      case RfcType.FORECAST:
        return "RFC_FORECAST";
      default:
        return "";
    }
  };

  const handleExecuteImport = async () => {
    try {
      setIsLoading(true);
      await rfcService.postFromListRoute({}, "execute");
      toast.success(translate.t("import_started_successfully") || "Importação iniciada com sucesso");
    } catch (error) {
      ErrorComponent(error as AxiosError, false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExecuteImportForecast = async (
    plant: string,
    customerId: string
  ) => {
    try {
      setIsLoading(true);
      const response = await forecastService.save({
        plant,
        customer_id: customerId,
      });
      toast.success(translate.t("import_started_successfully") || "Importação iniciada com sucesso");
      onTaskCreated?.((response.data as SapImportResponse)?.task_id);

      if (onSuccess) {
        onSuccess();
      }

      setIsLoading(false);
      return response.data;
    } catch (error) {
      setIsLoading(false);
      ErrorComponent(error as AxiosError, false);
      throw error;
    }
  };

  const handleExecuteImportStockPo = async () => {
    try {
      setIsLoading(true);
      const response = await stockPoService.save({
        plant: "451N",
      });
      toast.success(translate.t("import_started_successfully") || "Importação iniciada com sucesso");
      onTaskCreated?.((response.data as SapImportResponse)?.task_id);

      // Força reload do histórico para capturar o novo RFC em processamento
      if (onSuccess) {
        onSuccess();
      }

      setIsLoading(false);
      return response.data;
    } catch (error) {
      setIsLoading(false);
      ErrorComponent(error as AxiosError, false);
      throw error;
    }
  };

  const handleExecuteImportBom = async (
    plant: string,
    partNumbers: string[]
  ) => {
    try {
      setIsLoading(true);
      const response = await bomService.save({
        plant,
        part_numbers: partNumbers,
      });
      toast.success(translate.t("import_started_successfully") || "Importação iniciada com sucesso");
      onTaskCreated?.((response.data as SapImportResponse)?.task_id);

      // Força reload do histórico para capturar o novo RFC em processamento
      if (onSuccess) {
        onSuccess();
      }

      setIsLoading(false);
      return response.data;
    } catch (error) {
      setIsLoading(false);
      ErrorComponent(error as AxiosError, false);
      throw error;
    }
  };

  return {
    isLoading,
    showRrcFunctionName,
    handleExecuteImport,
    handleExecuteImportForecast,
    handleExecuteImportStockPo,
    handleExecuteImportBom,
    onSuccess,
  };
};
